import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { uploadImage, assetUrl, errMsg } from "@/lib/api";
import { Box, L } from "@/components/website/DesignTab";

export function ImagePick({ value, onChange, testId, label = "Upload" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { onChange(await uploadImage(f)); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="w-20 h-14 rounded-md border border-slate-700 bg-slate-800 overflow-hidden">{value && <img src={assetUrl(value)} alt="" className="w-full h-full object-cover" />}</div>
      <button type="button" className="cf-btn-ghost h-9 text-xs" onClick={() => ref.current?.click()} data-testid={testId}>{busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {label}</button>
      {value && <button type="button" className="p-1 text-slate-500 hover:text-red-400" onClick={() => onChange("")} data-testid={`${testId}-clear`}><X size={14} /></button>}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} />
    </div>
  );
}

export default function ContentTab({ site, set }) {
  const r = site.rates || {};
  const setRate = (k) => (e) => set({ rates: { ...r, [k]: e.target.value } });
  const inp = (k, ph, testId) => <input className="cf-input h-9 text-sm" placeholder={ph} value={site[k] || ""} onChange={(e) => set({ [k]: e.target.value })} data-testid={testId} />;
  return (
    <div className="space-y-6">
      <Box title="Today's gold rate" desc="Update daily — shown at the top of your website." testId="website-rates-box">
        <label className="flex items-center justify-between text-sm">
          Show gold rate on website
          <Switch checked={site.show_rates} onCheckedChange={(v) => set({ show_rates: v })} data-testid="website-show-rates-switch" />
        </label>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[["gold24", "24K Gold", "₹78,500"], ["gold22", "22K Gold", "₹72,000"], ["gold18", "18K Gold", "₹58,900"], ["silver", "Silver", "₹950"], ["note", "Unit", "per 10 g"]].map(([k, label, ph]) => (
            <L key={k} label={label}><input className="cf-input h-9 text-sm" placeholder={ph} value={r[k] || ""} onChange={setRate(k)} data-testid={`website-rate-${k}`} /></L>
          ))}
        </div>
      </Box>
      <Box title="Home banner" testId="website-hero-box">
        <div className="grid sm:grid-cols-2 gap-4">
          <L label="Headline">{inp("hero_title", "Timeless Jewellery", "website-hero-title-input")}</L>
          <L label="Sub-heading">{inp("hero_subtitle", "Certified gold & diamond jewellery", "website-hero-subtitle-input")}</L>
        </div>
        <L label="Banner photo" hint="Leave empty to use the theme photo"><ImagePick value={site.hero_image} onChange={(v) => set({ hero_image: v })} testId="website-hero-image" /></L>
      </Box>
      <Box title="About your store" desc="The About section appears when this text is filled." testId="website-about-box">
        <L label="Title">{inp("about_title", "Our story", "website-about-title-input")}</L>
        <L label="Text"><textarea rows={5} className="cf-input text-sm py-2" style={{ height: "auto" }} value={site.about || ""} onChange={(e) => set({ about: e.target.value })} placeholder="Three generations of trusted jewellers…" data-testid="website-about-input" /></L>
        <L label="Photo"><ImagePick value={site.about_image} onChange={(v) => set({ about_image: v })} testId="website-about-image" /></L>
      </Box>
      <Box title="Showroom details" desc="Name, logo, phone, WhatsApp and address come from your Store profile." testId="website-details-box">
        <div className="grid sm:grid-cols-2 gap-4">
          <L label="Opening hours">{inp("hours", "Mon–Sat · 10:30 am – 8:30 pm", "website-hours-input")}</L>
          <L label="Map location" hint="Address or place name for the map">{inp("map_query", "New Amina Jewellers, Islampur", "website-map-input")}</L>
        </div>
      </Box>
    </div>
  );
}
